import { useEffect, useRef } from "react";

import { fetchStaffOrders } from "@/lib/api";
import { convertPriceFromCents } from "@/lib/xReport";
import { openLiveFeed, type LiveOrder, type LiveTable } from "@/lib/liveFeed";
import { useAppDispatch, useAppSelector } from "@/store";
import { staffUid } from "@/store/slices/authSlice";
import { setResumedOrderId } from "@/store/slices/cartSlice";
import { setUpdateDineinTable } from "@/store/slices/dineinSlice";
import { setSnackbar } from "@/store/slices/snackbarSlice";
import {
  applyLiveOrder,
  isOpenBill,
  removeLiveOrder,
  setStaffOrders,
  setStaffOrdersLive,
  setStaffOrdersLoading,
} from "@/store/slices/staffOrdersSlice";
import type { StaffOrder } from "@/types";

/**
 * THE SUBSCRIPTION. One per staff session, mounted once in `_app`.
 *
 * It attaches `GET /web/live/:tenantId` — the same broadcast a POS terminal is
 * on, see `lib/liveFeed.ts` for why that is the right channel — and folds every
 * frame into three places:
 *
 * 1. **the floor plan**, through `setUpdateDineinTable`. That reducer already
 *    existed for the table-resume writeback and it takes exactly the payload
 *    `:4001` broadcasts (`_id` + `dinein_floor_id` + the seating fields), so a
 *    table seated, moved or FREED anywhere repaints here with no refresh and no
 *    poll;
 * 2. **the operator's open-bill list**, through `applyLiveOrder`. A bill that
 *    is no longer open drops out of the list rather than lingering;
 * 3. **the cart's adopted bill**. This is the one that matters most: if the
 *    basket has adopted an order and that order is settled, cancelled or
 *    deleted at the till, the adoption is RELEASED and the operator is told.
 *    Without it the next round would be PUT at a bill `:4001` would refuse with
 *    `ORDER_NOT_OPEN`, and the items would look lost.
 *
 * ## Why it is gated on `isStaff`
 *
 * A customer has no floor plan, no order list and no adopted bill to protect —
 * their basket is their own and their order page polls nothing. Opening a
 * long-lived stream for every visitor would cost a held connection per browser
 * for nothing. Staff-only, and it closes the moment the session ends.
 */

/** The stored report row, projected onto the shape the live channel speaks. */
const fromStaffOrder = (row: StaffOrder & Record<string, unknown>): LiveOrder => {
  const dinein = (row.dinein_details ?? null) as
    | { table_id?: unknown; floor_id?: unknown; table_name?: unknown; num_of_guests?: unknown }
    | null;
  return {
    _id: String(row._id),
    tenant_id: row.tenant_id ?? null,
    order_number: row.order_number ?? null,
    platform: row.platform ?? null,
    status: row.order_status ?? null,
    order_settled_at: (row.order_settled_at as string | null | undefined) ?? null,
    is_deleted: Boolean(row.is_deleted),
    // The report endpoint serves the STORED document, whose money is in CENTS.
    // The live channel already speaks major units, so the conversion belongs
    // here and only here.
    total: convertPriceFromCents(Number(row.grand_total ?? 0)),
    table_id: dinein?.table_id != null ? String(dinein.table_id) : null,
    floor_id: dinein?.floor_id != null ? String(dinein.floor_id) : null,
    table_name: dinein?.table_name != null ? String(dinein.table_name) : "",
    num_of_guests: Number(dinein?.num_of_guests ?? 0) || 0,
    order_taker_details: row.order_taker_details ?? null,
    updatedAt: row.updatedAt ?? null,
    createdAt: row.createdAt ?? null,
  };
};

export default function useLiveOrders() {
  const dispatch = useAppDispatch();
  const isStaff = useAppSelector((state) => state.auth.isStaff);
  const uid = useAppSelector((state) => staffUid(state.auth.staff));
  const tenantId = useAppSelector((state) => state.settings.data?._id);
  const resumedOrderId = useAppSelector((state) => state.cart.resumedOrderId);

  /**
   * The adopted bill is read through a REF, not through the effect's dependency
   * list. It changes every time a table is picked, and a dependency on it would
   * tear the stream down and re-seed the list on each pick — a reconnect storm
   * for a value only ever read inside a callback.
   */
  const resumedOrderIdRef = useRef<string | null>(resumedOrderId ?? null);
  useEffect(() => {
    resumedOrderIdRef.current = resumedOrderId ?? null;
  }, [resumedOrderId]);

  useEffect(() => {
    if (!isStaff || !tenantId || !uid) {
      dispatch(setStaffOrdersLive(false));
      return undefined;
    }

    let cancelled = false;

    /**
     * The REST seed, re-run on every (re)connection. A stream that dropped and
     * came back missed whatever happened while it was down, and `EventSource`
     * gives no replay — re-reading the operator's own bills is the cheap,
     * correct way to close that window.
     */
    const resync = async () => {
      dispatch(setStaffOrdersLoading(true));
      try {
        const rows = await fetchStaffOrders(uid, tenantId);
        if (cancelled) return;
        dispatch(
          setStaffOrders({
            orders: rows.map((row) => fromStaffOrder(row as StaffOrder & Record<string, unknown>)),
            uid,
          }),
        );
      } catch {
        // A failed seed is not an empty shift. Leave whatever is listed and let
        // the next reconnection try again; `loading` is cleared either way.
        if (!cancelled) dispatch(setStaffOrdersLoading(false));
      }
    };

    /**
     * A bill the CART has adopted has just changed state at the till. This is
     * the "if any order is cleared from the POS it must clear from here" case:
     * release the adoption so the next round opens a NEW bill instead of being
     * PUT at one `:4001` would refuse with `ORDER_NOT_OPEN`.
     */
    const releaseIfClosed = (order: LiveOrder) => {
      const adopted = resumedOrderIdRef.current;
      if (!adopted || order._id !== adopted) return;
      if (isOpenBill(order)) return;
      dispatch(setResumedOrderId(null));
      dispatch(
        setSnackbar({
          message:
            order.order_settled_at != null
              ? "That bill was settled on the POS. Anything you add now starts a new one."
              : "That bill was closed on the POS. Anything you add now starts a new one.",
          severity: "warning",
          anchorOrigin: { vertical: "top", horizontal: "center" },
        }),
      );
    };

    const close = openLiveFeed(tenantId, {
      onReady: () => {
        dispatch(setStaffOrdersLive(true));
        void resync();
      },
      onOrderCreated: (order) => {
        dispatch(applyLiveOrder(order));
      },
      onOrderUpdated: (order) => {
        dispatch(applyLiveOrder(order));
        releaseIfClosed(order);
      },
      onOrderDeleted: (orderId) => {
        dispatch(removeLiveOrder(orderId));
        if (resumedOrderIdRef.current && orderId === resumedOrderIdRef.current) {
          dispatch(setResumedOrderId(null));
          dispatch(
            setSnackbar({
              message: "That bill was removed on the POS. Anything you add now starts a new one.",
              severity: "warning",
              anchorOrigin: { vertical: "top", horizontal: "center" },
            }),
          );
        }
      },
      onTableUpdated: (table: LiveTable) => {
        // The reducer bails on a payload with no `dinein_floor_id`, which is
        // why `:4001` stamps it onto every broadcast table. Passed straight
        // through — the merge is `{ ...table, ...payload }`, so the layout
        // fields (position/angle/scale) a seating frame omits are preserved.
        dispatch(setUpdateDineinTable(table as never));
      },
    });

    return () => {
      cancelled = true;
      close();
      dispatch(setStaffOrdersLive(false));
    };
  }, [dispatch, isStaff, tenantId, uid]);
}
